import PropTypes from "prop-types"
import { useEffect, useRef, useState } from "react"
import Section from "components/Layout/Section"
import SimpleHeader from "components/Layout/SimpleHeader"
import * as css from "./MultiComparisonTable.module.css"

function getNavHeight() {
  // Measured live rather than hardcoded: the site nav's own height is
  // responsive (56px on desktop, 80px on mobile, per Header/index.js),
  // so a fixed offset either leaves a gap under it or sits behind it
  // depending on viewport width.
  const header = document.querySelector("header")
  return header ? header.getBoundingClientRect().height : 0
}

function competitorNameAndUrl(competitor) {
  return typeof competitor === "string"
    ? { name: competitor, url: null }
    : { name: competitor.name, url: competitor.url }
}

function MultiComparisonTable({
  label,
  heading,
  description,
  competitors = [],
  rows,
}) {
  const wrapperRef = useRef(null)
  const headerRowRef = useRef(null)
  // null when not stuck. Otherwise: viewport position/size to render the
  // floating header clone at, plus measured column widths so it lines up
  // pixel-for-pixel with the real (scrolled-away) header underneath.
  const [floating, setFloating] = useState(null)

  // Why a JS-positioned clone instead of `position: sticky` on the real
  // header cells: .tableWrapper needs `overflow-x: auto` for the
  // horizontal scroll / frozen Feature+Preset columns, and per the CSS
  // Overflow spec that always promotes its overflow-y to `auto` too —
  // which makes .tableWrapper itself (not the viewport) the sticky
  // containing block. Since .tableWrapper never actually scrolls
  // vertically (its height just matches its content), sticky-top has
  // nothing to react to there. A real viewport-relative freeze needs to
  // live outside that ancestor, which position: sticky can't do here.
  useEffect(() => {
    const wrapper = wrapperRef.current
    const headerRow = headerRowRef.current
    if (!wrapper || !headerRow) return undefined

    let cachedColWidths = null

    function measureColumnWidths() {
      return Array.from(headerRow.children).map(
        (th) => th.getBoundingClientRect().width
      )
    }

    function update() {
      const stickTop = getNavHeight()
      const rect = wrapper.getBoundingClientRect()
      const headerHeight = headerRow.getBoundingClientRect().height
      const shouldStick =
        rect.top < stickTop && rect.bottom > stickTop + headerHeight

      if (!shouldStick) {
        if (cachedColWidths) cachedColWidths = null
        setFloating(null)
        return
      }

      if (!cachedColWidths) {
        cachedColWidths = measureColumnWidths()
      }
      const [featureWidth = 0, presetWidth = 0, ...competitorWidths] =
        cachedColWidths

      setFloating({
        top: stickTop,
        left: rect.left,
        width: rect.width,
        frozenWidth: featureWidth + presetWidth,
        featureWidth,
        presetWidth,
        competitorWidths,
        scrollLeft: wrapper.scrollLeft,
      })
    }

    function handleResize() {
      cachedColWidths = null
      update()
    }

    update()
    window.addEventListener("scroll", update, { passive: true })
    window.addEventListener("resize", handleResize)
    wrapper.addEventListener("scroll", update, { passive: true })
    return () => {
      window.removeEventListener("scroll", update)
      window.removeEventListener("resize", handleResize)
      wrapper.removeEventListener("scroll", update)
    }
  }, [competitors.length])

  return (
    <Section padding="light">
      <SimpleHeader
        label={label}
        title={heading}
        subtitle={description}
        dash="vertical"
        narrow="both"
      />
      <div className={css.tableWrapper} ref={wrapperRef}>
        <table className={css.table}>
          <thead>
            <tr
              ref={headerRowRef}
              style={floating ? { visibility: "hidden" } : undefined}
            >
              <th className={css.featureHeader}>Feature</th>
              <th className={css.presetHeader}>Preset</th>
              {competitors.map((competitor, idx) => {
                const { name, url } = competitorNameAndUrl(competitor)
                return (
                  <th key={idx} className={css.competitorHeader}>
                    {url ? <a href={url}>{name}</a> : name}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {rows?.map((row, index) => (
              <tr key={index} className={css.row}>
                <td className={css.feature}>{row.feature}</td>
                <td className={css.presetCell}>
                  {row.preset === true ? (
                    <span className={css.check}>✓</span>
                  ) : row.preset === false ? (
                    <span className={css.x}>✗</span>
                  ) : (
                    <span className={css.note}>{row.preset}</span>
                  )}
                  {row.presetNote && (
                    <span className={css.cellNote}>{row.presetNote}</span>
                  )}
                </td>
                {competitors.map((_, idx) => {
                  const value = row.competitors?.[idx]
                  const note = row.competitorNotes?.[idx]
                  return (
                    <td key={idx} className={css.competitorCell}>
                      {value === true ? (
                        <span className={css.check}>✓</span>
                      ) : value === false ? (
                        <span className={css.x}>✗</span>
                      ) : (
                        <span className={css.note}>{value}</span>
                      )}
                      {note && <span className={css.cellNote}>{note}</span>}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {floating && (
        <div
          className={css.floatingHeader}
          style={{
            top: floating.top,
            left: floating.left,
            width: floating.width,
          }}
        >
          <table className={css.table} style={{ width: floating.width }}>
            <thead>
              <tr>
                <th
                  className={css.featureHeader}
                  style={{
                    position: "static",
                    width: floating.featureWidth,
                    minWidth: floating.featureWidth,
                    maxWidth: floating.featureWidth,
                  }}
                >
                  Feature
                </th>
                <th
                  className={css.presetHeader}
                  style={{
                    position: "static",
                    width: floating.presetWidth,
                    minWidth: floating.presetWidth,
                    maxWidth: floating.presetWidth,
                  }}
                >
                  Preset
                </th>
                <th
                  colSpan={competitors.length}
                  style={{ padding: 0, border: "none" }}
                >
                  <div
                    className={css.floatingScrollClip}
                    style={{ width: floating.width - floating.frozenWidth }}
                  >
                    <table
                      className={css.table}
                      style={{
                        width: floating.competitorWidths.reduce(
                          (a, b) => a + b,
                          0
                        ),
                        transform: `translateX(-${floating.scrollLeft}px)`,
                      }}
                    >
                      <thead>
                        <tr>
                          {competitors.map((competitor, idx) => {
                            const { name, url } =
                              competitorNameAndUrl(competitor)
                            return (
                              <th
                                key={idx}
                                className={css.competitorHeader}
                                style={{
                                  width: floating.competitorWidths[idx],
                                  minWidth: floating.competitorWidths[idx],
                                }}
                              >
                                {url ? <a href={url}>{name}</a> : name}
                              </th>
                            )
                          })}
                        </tr>
                      </thead>
                    </table>
                  </div>
                </th>
              </tr>
            </thead>
          </table>
        </div>
      )}
    </Section>
  )
}

MultiComparisonTable.propTypes = {
  label: PropTypes.string,
  heading: PropTypes.string,
  description: PropTypes.string,
  competitors: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ name: PropTypes.string, url: PropTypes.string }),
    ])
  ),
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      feature: PropTypes.string.isRequired,
      preset: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
      presetNote: PropTypes.string,
      competitors: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
      ),
      competitorNotes: PropTypes.arrayOf(PropTypes.string),
    })
  ),
}

export default MultiComparisonTable
